import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActionArea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Skeleton,
  Fade,
  Drawer,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import InboxIcon from "@mui/icons-material/Inbox";
import { toast } from "react-toastify";
import { commissionsApi } from "../../Components/commissionsApi";
import { GlobalSearchBar } from "../../Components/ERPList";
import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import { RoleContext } from "../../Contexts/RoleContext";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import styles from "../AllEnquiries.module.scss";

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
};

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.mode === "dark" ? undefined : "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[10],
  },
}));

const SOURCE_LABELS = {
  total_business_revenue: "Total Business Revenue",
  total_conversions: "Total Conversions",
  conversions_by_program: "Conversions by Program",
  conversions_by_course: "Conversions by Course",
};

function formatSource(source) {
  return SOURCE_LABELS[source] || source;
}

function AllCommissions() {
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const { role } = useContext(RoleContext);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedSearch = useDebouncedValue(globalSearch, 400);
  const [activeFilter, setActiveFilter] = useState("true");
  const [filterOpen, setFilterOpen] = useState(!isMobile);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const canManage = role === "admin" || role === "Manager";

  const fetchStructures = async () => {
    setLoading(true);
    try {
      const res = await commissionsApi.list({
        active: activeFilter === "all" ? undefined : activeFilter,
        search: debouncedSearch.trim() || undefined,
      });
      setStructures(res.structures || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load commission structures");
      setStructures([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStructures();
  }, [debouncedSearch, activeFilter]);

  const resetFilters = () => {
    setGlobalSearch("");
    setActiveFilter("true");
    toast.info("Filters cleared.");
  };

  const handleView = (s) => {
    navigate(`/commissions/${s.id}`);
  };

  const handleEdit = (s) => {
    navigate(`/commissions/${s.id}/edit`);
  };

  const handleDeleteClick = (s) => setDeleteId(s);
  const handleDeleteClose = () => {
    if (!deleteLoading) setDeleteId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await commissionsApi.delete(deleteId.id);
      toast.success("Commission structure deleted.");
      setDeleteId(null);
      fetchStructures();
    } catch (err) {
      toast.error(err?.message || "Failed to delete");
    } finally {
      setDeleteLoading(false);
    }
  };

  const displayStructures = structures;

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
              <Box sx={{ marginBottom: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <Box>
                    <Typography variant="h4" className={styles.pageTitle}>
                      Commission Structures
                    </Typography>
                    <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                      Create and manage commission structures for employees.
                    </Typography>
                  </Box>
                  {canManage && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => navigate("/commissions/create")}
                    >
                      Create Commission Structure
                    </Button>
                  )}
                </Stack>
              </Box>

              <Box className={styles.header}>
                <GlobalSearchBar
                  value={globalSearch}
                  onChange={setGlobalSearch}
                  placeholder="Search name, description…"
                />
                <Box className={styles.headerActions}>
                  <Button
                    startIcon={<FilterListIcon />}
                    onClick={() => setFilterOpen(!filterOpen)}
                    variant="outlined"
                    size="small"
                  >
                    {filterOpen ? "Hide Filters" : "Show Filters"}
                  </Button>
                </Box>
              </Box>

              {filterOpen && (
                <Fade in={filterOpen}>
                  <FilterPaper variant="outlined">
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={activeFilter}
                            label="Status"
                            onChange={(e) => setActiveFilter(e.target.value)}
                          >
                            <MenuItem value="true">Active</MenuItem>
                            <MenuItem value="false">Inactive</MenuItem>
                            <MenuItem value="all">All</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} className={styles.filterActions}>
                        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>
                          Reset
                        </Button>
                      </Grid>
                    </Grid>
                  </FilterPaper>
                </Fade>
              )}

              {loading && (
                <Box className={styles.loadingContainer}>
                  <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
                  <Skeleton variant="rectangular" height={400} />
                </Box>
              )}

              {!loading && displayStructures.length === 0 && (
                <Box className={styles.emptyState}>
                  <AccountBalanceIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No commission structures found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting filters or create a new structure.
                  </Typography>
                  {canManage && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => navigate("/commissions/create")}
                      sx={{ mt: 2 }}
                    >
                      Create Commission Structure
                    </Button>
                  )}
                </Box>
              )}

              {!loading && displayStructures.length > 0 && (
                <Paper variant="outlined" className={styles.tableContainer}>
                  <Table stickyHeader size="small" className={styles.enquiriesTable}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.colName}>Commission Name</TableCell>
                        <TableCell>Timeframe</TableCell>
                        <TableCell>Sources</TableCell>
                        <TableCell>Assigned Users</TableCell>
                        <TableCell>Active Status</TableCell>
                        <TableCell>Created Date</TableCell>
                        {canManage && <TableCell className={styles.colActions}>Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayStructures.map((s) => (
                        <TableRow
                          key={s.id}
                          hover
                          onClick={() => handleView(s)}
                          className={styles.tableRow}
                        >
                          <TableCell className={styles.colName}>
                            <Typography variant="body2" fontWeight={500}>
                              {s.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{s.timeframe ? String(s.timeframe).charAt(0).toUpperCase() + s.timeframe.slice(1) : "—"}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {(s.sources || []).slice(0, 3).map((src) => (
                                <Chip key={src} label={formatSource(src)} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                              ))}
                              {(s.sources || []).length > 3 && (
                                <Chip label={`+${s.sources.length - 3}`} size="small" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>{s.assigned_count ?? 0}</TableCell>
                          <TableCell>
                            <Chip
                              label={s.active ? "Active" : "Inactive"}
                              color={s.active ? "success" : "default"}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                          </TableCell>
                          {canManage && (
                            <TableCell className={styles.colActions} onClick={(e) => e.stopPropagation()}>
                              <Stack direction="row" spacing={0.5} className={styles.actionButtons}>
                                <Tooltip title="View">
                                  <IconButton size="small" onClick={() => handleView(s)}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => handleEdit(s)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton size="small" color="error" onClick={() => handleDeleteClick(s)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </Paper>

        <Dialog open={!!deleteId} onClose={handleDeleteClose}>
          <DialogTitle>Delete Commission Structure</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete &quot;{deleteId?.name}&quot;? This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClose} disabled={deleteLoading}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleteLoading}>
              {deleteLoading ? "Deleting…" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}

export default AllCommissions;
